import { BACKEND_URL } from '@/lib/api';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MigrationBanner from "@/components/MigrationBanner";
import DashboardLayout from "@/components/DashboardLayout";
import MboaSendLanding from "@/pages/send/MboaSendLanding";
import MboaSendLogin from "@/pages/send/MboaSendLogin";
import MboaSendRegister from "@/pages/send/MboaSendRegister";
import MboaSendDashboard from "@/pages/send/MboaSendDashboard";

// Pages loaded on demand: a client never downloads the admin or agent screens.
// After a new deployment an old tab may ask for a file that no longer exists: reload once to get the new version.
const CHUNK_KEY = 'mboa-chunk-reload';
const lazyPage = (load) => lazy(() => load().then((m) => {
  try { sessionStorage.removeItem(CHUNK_KEY); } catch (e) { /* storage unavailable */ }
  return m;
}).catch((err) => {
  let retried = true;
  try { retried = !!sessionStorage.getItem(CHUNK_KEY); if (!retried) sessionStorage.setItem(CHUNK_KEY, '1'); } catch (e) { /* storage unavailable */ }
  if (!retried) { window.location.reload(); return new Promise(() => {}); }
  throw err;
}));
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#006400] border-t-transparent rounded-full animate-spin" />
  </div>
);

const InstallGuide = lazyPage(() => import("@/pages/InstallGuide"));
const Security = lazyPage(() => import("@/pages/Security"));
const VerifyEmail = lazyPage(() => import("@/pages/VerifyEmail"));
const ResetPassword = lazyPage(() => import("@/pages/ResetPassword"));
const CheckEmail = lazyPage(() => import("@/pages/CheckEmail"));
const KYC = lazyPage(() => import("@/pages/user/KYC"));
const Notifications = lazyPage(() => import("@/pages/user/Notifications"));
const Profile = lazyPage(() => import("@/pages/user/Profile"));
const AgentDashboard = lazyPage(() => import("@/pages/agent/AgentDashboard"));
const AdminDashboard = lazyPage(() => import("@/pages/admin/AdminDashboard"));
const MboaSendVerifyEmail = lazyPage(() => import("@/pages/send/MboaSendVerifyEmail"));
const PrivacyPolicy = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.TermsOfService })));
const AmlKycPolicy = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.AmlKycPolicy })));
const RefundPolicy = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.RefundPolicy })));
const LegalNotice = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.LegalNotice })));
const SendMoney = lazyPage(() => import("@/pages/send/SendMoney"));
const SendDeposit = lazyPage(() => import("@/pages/send/SendDeposit"));
const SendHistory = lazyPage(() => import("@/pages/send/SendHistory"));
const SendDetail = lazyPage(() => import("@/pages/send/SendDetail"));
const ReferralDashboard = lazyPage(() => import("@/pages/send/ReferralDashboard"));
const ColisSearch = lazyPage(() => import("@/pages/colis/ColisSearch"));
const ColisPublish = lazyPage(() => import("@/pages/colis/ColisPublish"));
const ColisBook = lazyPage(() => import("@/pages/colis/ColisBook"));
const ColisMyBookings = lazyPage(() => import("@/pages/colis/ColisMyBookings"));
const ColisMyTrips = lazyPage(() => import("@/pages/colis/ColisMyTrips"));
const ColisPro = lazyPage(() => import("@/pages/colis/ColisPro"));
const PublicTracking = lazyPage(() => import("@/pages/colis/PublicTracking"));
const SendColisLanding = lazyPage(() => import("@/pages/colis/SendColisLanding"));
const CGUSendColis = lazyPage(() => import("@/pages/colis/CGUSendColis"));

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#006400] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (['admin','super_admin'].includes(user.role)) return <Navigate to="/admin" />;
    if (user.role === 'agent') return <Navigate to="/agent" />;
    return <Navigate to="/send/dashboard" />;
  }
  // Admins accessing non-admin pages get redirected to admin
  if (!allowedRoles && ['admin','super_admin'].includes(user.role)) {
    const path = window.location.pathname;
    if (path.startsWith('/send/') || path.startsWith('/dashboard') || path === '/') {
      return <Navigate to="/admin" />;
    }
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (['admin','super_admin'].includes(user.role)) return <Navigate to="/admin" />;
    if (user.role === 'agent') return <Navigate to="/agent" />;
    return <Navigate to="/send/dashboard" />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<MboaSendLanding />} />
      <Route path="/login" element={<MboaSendLogin />} />
      <Route path="/register" element={<MboaSendRegister />} />
      <Route path="/mboasend/login" element={<MboaSendLogin />} />
      <Route path="/mboasend/register" element={<MboaSendRegister />} />
      <Route path="/mboasend/verify-email" element={<MboaSendVerifyEmail />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />

      <Route path="/dashboard" element={<Navigate to="/send/dashboard" />} />
      <Route path="/referral" element={<Navigate to="/send/referral" />} />
      <Route path="/kyc" element={
        <ProtectedRoute>
          <DashboardLayout><KYC /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>          <DashboardLayout><Notifications /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/securite" element={
        <ProtectedRoute>
          <Security />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardLayout><Profile /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/agent" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <DashboardLayout><AgentDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/agent/*" element={
        <ProtectedRoute allowedRoles={['agent']}>
          <DashboardLayout><AgentDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin','super_admin']}>
          <DashboardLayout><AdminDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin','super_admin']}>
          <DashboardLayout><AdminDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* MboaSend Routes */}
      <Route path="/send" element={<MboaSendLanding />} />
      <Route path="/installer" element={<InstallGuide />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-use" element={<TermsOfService />} />
      <Route path="/aml-kyc" element={<AmlKycPolicy />} />
      <Route path="/aml-kyc-policy" element={<AmlKycPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/legal-notice" element={<LegalNotice />} />
      <Route path="/track/:code" element={<PublicTracking />} />
      <Route path="/track" element={<PublicTracking />} />
      <Route path="/sendcolis" element={<SendColisLanding />} />
      <Route path="/cgu-sendcolis" element={<CGUSendColis />} />

      <Route path="/send/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout><MboaSendDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/send/new" element={
        <ProtectedRoute>
          <DashboardLayout><SendMoney /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/send/deposit" element={
        <ProtectedRoute>
          <DashboardLayout><SendDeposit /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/send/history" element={
        <ProtectedRoute>
          <DashboardLayout><SendHistory /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/send/:id" element={
        <ProtectedRoute>
          <DashboardLayout><SendDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/send/referral" element={
        <ProtectedRoute>
          <DashboardLayout><ReferralDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Send Colis Routes */}
      <Route path="/colis" element={<ProtectedRoute><DashboardLayout><ColisSearch /></DashboardLayout></ProtectedRoute>} />
      <Route path="/colis/publish" element={<ProtectedRoute><DashboardLayout><ColisPublish /></DashboardLayout></ProtectedRoute>} />
      <Route path="/colis/trip/:id" element={<ProtectedRoute><DashboardLayout><ColisBook /></DashboardLayout></ProtectedRoute>} />
      <Route path="/colis/my-bookings" element={<ProtectedRoute><DashboardLayout><ColisMyBookings /></DashboardLayout></ProtectedRoute>} />
      <Route path="/colis/my-trips" element={<ProtectedRoute><DashboardLayout><ColisMyTrips /></DashboardLayout></ProtectedRoute>} />
      <Route path="/colis/pro" element={<ProtectedRoute><DashboardLayout><ColisPro /></DashboardLayout></ProtectedRoute>} />



      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
}

const WhatsAppButton = () => {
  const [number, setNumber] = useState('237621055365');
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/config/public`)
      .then(r => r.json())
      .then(d => { if (d.whatsapp_number) setNumber(d.whatsapp_number); })
      .catch(() => {});
  }, []);
  return (
    <a
      href={`https://wa.me/${number}?text=Bonjour%20MBOA%2C%20j%27ai%20besoin%20d%27aide`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-support-btn"
      className="fixed bottom-20 md:bottom-5 right-5 z-40 w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#20BA5A] active:scale-95 transition-transform"
      style={{ boxShadow: '0 4px 15px rgba(37,211,102,0.4)' }}
    >
      <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

function App() {
  return (
    <BrowserRouter>
    <div className="notranslate" translate="no">
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
        <MigrationBanner />
      </AuthProvider>
    </div>
    </BrowserRouter>
  );
}

export default App;
